import { TextInput, ConfirmModal, CustomButton } from '@/shared/ui/components';
import { getContent } from '@/entities/study';
import { inforBlackIcon, clearIcon } from '@/shared/ui/assets';
import { Drawer, Select } from 'antd';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { useEditDiagnosisDrawer } from './useEditDiagnosisDrawer';
import type { EditDiagnosisDrawerProps } from './editDiagnosisDrawer.types';

const EditDiagnosisDrawer = ({
  onClose,
  open,
  data,
  updateStudyData,
  drawerKey,
  ...params
}: EditDiagnosisDrawerProps) => {
  const {
    state,
    setState,
    isAbortedStudy,
    handleChange,
    handleSearchSelectPrimary,
    handleSearchSelectSecond,
    handlePopupScrollPrimary,
    handlePopupScrollSecond,
    handleClickSave,
    handleOnClose,
    onSave,
  } = useEditDiagnosisDrawer({ onClose, open, data, updateStudyData, drawerKey });

  // tag title
  return (
    <Drawer
      {...params}
      open={open}
      rootClassName="edit-study-drawer-wrapper"
      title={t`Diagnosis`}
      aria-label={t`Diagnosis`}
      onClose={handleOnClose}
      destroyOnClose
      maskClosable={false}
    >
      {state.isInitiating ? (
        <div className="edit-study-details">
          <Skeleton className="initiating-skeleton" />
        </div>
      ) : (
        <>
          <ConfirmModal
            title={t`Apply changes`}
            question={getContent()}
            isOpen={state.isOpenConfirmModal}
            leftButtonName={t`Cancel`}
            rightButtonName={t`Apply changes`}
            onClickLeftButton={() => setState({ isOpenConfirmModal: false })}
            onClickRightButton={() => onSave()}
            isSaving={state.isSaving}
          />

          <div className="edit-study-details">
            <div className="content-container">
              <div className="content">
                <div className="right">
                  <div>
                    <div className="diagnosis-code-select">
                      <div className="diagnosis-code-select__label">
                        {t`Primary diagnosis code`}
                      </div>
                      <Select
                        className="custom-select-test"
                        style={{ width: '100%' }}
                        name="primary"
                        showSearch
                        onSearch={handleSearchSelectPrimary}
                        placeholder={t`Search & select a code`}
                        options={state.diagnosisCodesPrimary}
                        onChange={(value) => handleChange('primary', value)}
                        onPopupScroll={handlePopupScrollPrimary as (e: any) => void}
                        size="middle"
                        filterOption={false}
                        value={state.primary}
                        loading={state.isLoadingPrimary}
                        notFoundContent={
                          <div className="empty-select">
                            {state.isLoadingPrimary
                              ? `${t`Loading`}...`
                              : state.searchPrimary
                                ? t`No diagnosis code matches your search`
                                : t`Search by code or diagnosis name`}
                          </div>
                        }
                        id="select-primary-diagnosis-code"
                        aria-label={t`Primary diagnosis code`}
                      />
                    </div>

                    {isAbortedStudy && (
                      <div className="abort-input">
                        <img src={inforBlackIcon} alt="inforBlackIcon" />
                        {t`Cannot edit primary diagnosis code of aborted study.`}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="diagnosis-code-select">
                      <div className="diagnosis-code-select__label">
                        {t`Secondary diagnosis code`}{' '}
                        <span className="optional-label">(optional)</span>
                      </div>
                      <Select
                        className="custom-select-test"
                        style={{ width: '100%' }}
                        name="second"
                        showSearch
                        onSearch={handleSearchSelectSecond}
                        placeholder={t`Search & select a code`}
                        options={state.diagnosisCodesSecond}
                        onChange={(value) => handleChange('second', value)}
                        onPopupScroll={handlePopupScrollSecond as (e: any) => void}
                        size="middle"
                        filterOption={false}
                        value={state.second}
                        loading={state.isLoadingSecond}
                        notFoundContent={
                          <div className="empty-select">
                            {state.isLoadingSecond
                              ? `${t`Loading`}...`
                              : state.searchSecond
                                ? t`No diagnosis code matches your search`
                                : t`Search by code or diagnosis name`}
                          </div>
                        }
                        allowClear={{
                          clearIcon: <img src={clearIcon} alt="Clear icon" />,
                        }}
                        id="select-secondary-diagnosis-code"
                        aria-label={t`Secondary diagnosis code`}
                      />
                    </div>

                    {isAbortedStudy && (
                      <div className="abort-input">
                        <img src={inforBlackIcon} alt="inforBlackIcon" />
                        {t`Cannot edit secondary diagnosis code of aborted study.`}
                      </div>
                    )}
                  </div>

                  <div>
                    <TextInput
                      className="has-default-height"
                      type="textarea"
                      name="patientMedications"
                      label={
                        <>
                          {t`Patient medication`} <span className="optional-label">(optional)</span>
                        </>
                      }
                      value={state.patientMedications}
                      isError={!!state.listError.patientMedications}
                      errorMessage={state.listError.patientMedications}
                      placeholder={t`Note`}
                      onChange={handleChange}
                      disabled={isAbortedStudy}
                      id="input-patient-medication"
                    />
                    {isAbortedStudy && (
                      <div className="abort-input">
                        <img src={inforBlackIcon} alt="inforBlackIcon" />
                        {t`Cannot edit patient medication of aborted study.`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="edit-study-button-container">
              <CustomButton
                id="cancel-btn"
                type="outline"
                style="neutral"
                size="middle"
                onClick={handleOnClose}
              >
                {t`Cancel`}
              </CustomButton>

              <CustomButton
                id="save-btn"
                type="primary"
                style="default"
                size="middle"
                onClick={handleClickSave}
              >
                {t`Save`}
              </CustomButton>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default EditDiagnosisDrawer;
